import { useRef } from "react";
import cx from "classnames";
import { Dropdown, DropdownSelectionType } from "@repo/ping-design";
import type { RenderEditCellProps } from "react-data-grid";
import { useCellSave } from "../useCellSave";
import type { EditableFieldConfig } from "../editableFields";
import type { TopBuildingsListRow } from "../TopBuildingsList";

type Props = RenderEditCellProps<TopBuildingsListRow> & {
  config: EditableFieldConfig;
  onSave: (value: string) => Promise<boolean>;
};

export const SEARCHABLE_OPTION_COUNT = 8;
export const COPE_DROPDOWN_WIDTH = "24rem";
export const ZONE_DROPDOWN_WIDTH = "150px";

export function DropdownCellEditor({ row, column, config, onSave, onClose }: Props) {
  const { status, runSave } = useCellSave();
  // Track whether onChange fired so openChanged(false) can distinguish
  // a dismiss-without-selection from a post-selection close.
  // Note: Dropdown calls setIsOpen(false) directly on item click, which does
  // NOT fire openChanged — so this ref guards only genuine dismissals.
  const committedRef = useRef(false);
  const options = config.options ?? [];
  const isCodeList = options.length > SEARCHABLE_OPTION_COUNT;

  return (
    <div
      className={cx("TopBuildingsList__CellDropdownWrapper", {
        "TopBuildingsList__CellDropdownWrapper--Error": status === "error",
      })}
    >
      <Dropdown
        options={options}
        selected={String(row.values[column.key] ?? "")}
        defaultOpen
        searchable={options.length > SEARCHABLE_OPTION_COUNT}
        searchPlaceholder={isCodeList ? "Search code or name" : "Filter"}
        dropdownWidth={isCodeList ? COPE_DROPDOWN_WIDTH : ZONE_DROPDOWN_WIDTH}
        menuClasses="TopBuildingsList__CellDropdownMenu"
        menuItemClasses="TopBuildingsList__CellDropdownMenu__Item"
        disabled={status === "saving"}
        error={status === "error"}
        onChange={async (sel) => {
          if (sel.type !== DropdownSelectionType.SimpleValue) return;
          committedRef.current = true;
          const ok = await runSave(() => onSave(sel.value));
          if (ok) {
            onClose(true);
          } else {
            committedRef.current = false;
          }
        }}
        openChanged={(open) => {
          if (!open && !committedRef.current) {
            onClose(false);
          }
        }}
      />
    </div>
  );
}
